/* eslint-disable-next-line import/no-unresolved */
import { MARKS } from '@contentful/rich-text-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { pageIntroContent } from '@insify/css/classes.module.css';
import { Button, Select } from '@insify/design-system/components';
import { formatCents } from '@insify/ui-utils';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';

import { InputCents } from '../../../../components/InputMoney';
import { RichContentWithTooltip } from '../../../../components/RichContent';
import { usePastClaims } from '../../usePastClaims';
import { useClaimOptions } from '../useClaimOptions';

import * as styles from './ClaimsEdit.module.css';

import type { PastClaimsContent } from '../../PastClaims';
import type { PastClaim } from '../../usePastClaims';
import type { MoneyField } from '@insify/global-money';
import type React from 'react';

const defaultValidationRules = {
  minClaimAmount: 100,
  maxClaimAmount: 9999999900,
};

export const ClaimsEditForm = <CoverageKey extends string>({
  richContent: { form },
  translations,
  validation = defaultValidationRules,
  currencyPosition,
  thousandSeparator,
}: // validation,
PastClaimsContent): JSX.Element => {
  const { previousYearsOptions } = useClaimOptions();
  const [{ pastClaimFormState, coverageOptions, pastClaimThreshold }, { addClaim, updateClaim, closePastClaimForm }] =
    usePastClaims<CoverageKey>();
  const { handleSubmit, formState, register } = useForm<PastClaim<string>>({
    resolver: yupResolver(
      yup.object({
        year: yup.string().required(translations.validationYearRequired),
        coverage: yup.string().required(translations.validationCoverageRequired),
        amount: yup.object({
          cents: yup
            .number()
            .typeError(translations.validationAmountRequired)
            .min(validation.minClaimAmount, translations.validationNumberTooLow)
            .max(validation.maxClaimAmount, translations.validationNumberTooHigh)
            .required(translations.validationAmountRequired),
        }),
      }),
    ),
    defaultValues: pastClaimFormState,
  });

  const limit = pastClaimThreshold ? formatCents(pastClaimThreshold.cents, { decimals: false }) : '';

  function submitClaim(claim: PastClaim<CoverageKey>): void {
    const value = {
      ...claim,
      id: claim.id ?? uuid(),
      amount: { cents: claim.amount.cents.toString(), currency: 'EUR' } as MoneyField,
    };
    !pastClaimFormState?.id ? addClaim(value) : updateClaim(value);
    closePastClaimForm();
  }

  const [renderMark, setRenderMark] = useState({});
  useEffect(() => {
    setRenderMark({
      [MARKS.CODE]: (children: React.ReactNode): React.ReactNode => <>{children === 'limit' ? limit : children}</>,
    });
  }, [limit]);

  return (
    <>
      <RichContentWithTooltip content={form} renderMark={renderMark} className={pageIntroContent} />
      <form onSubmit={handleSubmit(submitClaim as (pastClaim: PastClaim<string>) => void)} noValidate={true}>
        <Select
          {...register('year')}
          trackingId='year'
          id='selectYear'
          error={formState.errors.year?.message}
          label={translations.formYearLabel}
          placeholder={translations.formYearPlaceholder}
          required={true}
        >
          {previousYearsOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Select
          {...register('coverage')}
          trackingId='coverage'
          id='selectCoverage'
          error={formState.errors.coverage?.message}
          label={translations.formCoverageLabel}
          placeholder={translations.formCoveragePlaceholder}
          required={true}
        >
          {coverageOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <InputCents
          {...register('amount.cents')}
          label={translations.formAmountLabel}
          trackingId='claim-amount'
          id='claim-amount'
          decimalScale={0}
          before={currencyPosition === 'before' ? translations.currency : undefined}
          after={currencyPosition === 'after' ? translations.currency : undefined}
          placeholder='0'
          error={formState.errors.amount?.cents?.message}
          thousandSeparator={thousandSeparator}
        />
        <Button type='submit' kind='primary' trackingId='continue' className={styles.button}>
          {translations.formSubmit}
        </Button>
      </form>
    </>
  );
};
